/**
 * Cálculo e formatação do total mensal da extensão.
 *
 * O spec §10.1 deixa o recálculo como client-side no MVP: os preços chegam no
 * `resolve` e o total é somado no browser conforme a retenção de dependentes do
 * Step 3. Como o titular confirma um valor que viu na tela, este módulo é puro e
 * valida as entradas em vez de propagar `NaN` para dentro da UI.
 */

import { IEligiblePlan } from "./types";

/** Erro de cálculo de preço, para a UI distinguir de falha de rede. */
export class PricingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PricingError";
  }
}

function assertPriceInCents(value: number, field: string): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new PricingError(
      `${field} deve ser um inteiro não negativo em centavos, recebido: ${value}.`
    );
  }
}

/**
 * Total mensal em centavos: mensalidade do titular mais uma parcela por
 * dependente retido.
 */
export function calculateMonthlyTotalCents(
  plan: Pick<IEligiblePlan, "holderOnlyPriceCents" | "perDependentPriceCents">,
  retainedDependentCount: number
): number {
  assertPriceInCents(plan.holderOnlyPriceCents, "holderOnlyPriceCents");
  assertPriceInCents(plan.perDependentPriceCents, "perDependentPriceCents");

  if (!Number.isInteger(retainedDependentCount) || retainedDependentCount < 0) {
    throw new PricingError(
      `Quantidade de dependentes retidos inválida: ${retainedDependentCount}.`
    );
  }

  return (
    plan.holderOnlyPriceCents +
    plan.perDependentPriceCents * retainedDependentCount
  );
}

/** Formata centavos como moeda brasileira. Ex.: 124000 → "R$ 1.240,00". */
export function formatCents(cents: number): string {
  assertPriceInCents(cents, "cents");

  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}
